import axios from 'axios';
import { User, UserRole } from '../types/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export interface LoginResponse {
    user: User;
    token: string;
    message: string;
}

export const authService = {
    /**
     * Google OAuth 登入
     */
    async loginWithGoogle(googleToken: string): Promise<LoginResponse> {
        const response = await apiClient.post<{ data: LoginResponse }>('/api/auth/google', {
            token: googleToken,
        });
        return response.data.data;
    },

    /**
     * 登出
     */
    async logout(): Promise<void> {
        await apiClient.post('/api/auth/logout');
        localStorage.removeItem('auth_token');
    },

    /**
     * 獲取當前用戶
     */
    async getCurrentUser(): Promise<User> {
        const response = await apiClient.get<{ data: { user: User } }>('/api/auth/me');
        return response.data.data.user;
    },

    /**
     * 獲取所有用戶（管理員）
     */
    async getAllUsers(): Promise<User[]> {
        const response = await apiClient.get<{ data: { users: User[] } }>('/api/users');
        return response.data.data.users;
    },

    /**
     * 更新用戶角色（管理員）
     */
    async updateUserRole(email: string, role: UserRole): Promise<User> {
        const response = await apiClient.put<{ data: { user: User } }>(
            `/api/users/${encodeURIComponent(email)}/role`,
            { role }
        );
        return response.data.data.user;
    },

    /**
     * 創建用戶（管理員）
     */
    async createUser(email: string, name: string, role: UserRole): Promise<User> {
        const response = await apiClient.post<{ data: { user: User } }>('/api/users', {
            email,
            name,
            role,
        });
        return response.data.data.user;
    },
};

export default authService;
